import { LocalDate } from '@js-joda/core';
import { useMemo } from 'react';

import { FiscalDocument } from '@dreryk/egabinet-invoice-frontend-api';

import { ModalConfirmButton } from '@dreryk/react-components';

import { invoiceCreateModalTranslationKeys } from '../translations';
import { InvoicePaymentMethod } from '../types';

import { InvoiceCreateFormController } from './form/InvoiceCreateForm.types';
import { fiscalDocumentTypeVisitor } from './form/InvoiceCreateForm.utils';
import { usePrivateIndividualInvoiceCreateCommand } from './PrivateIndividualInvoiceCreateModalConfirmButton.hooks';
import { WithReceiptModalOpener } from './withReceipt/WithReceiptModal.opener';

type PrivateIndividualInvoiceCreateModalConfirmButtonProps = Pick<InvoiceCreateFormController, 'handleSubmit'> & {
   readonly paymentMethod: InvoicePaymentMethod;
   readonly visitDate: LocalDate;
   readonly onClose: VoidFunction;
   readonly visitSettlementId: number;
   readonly fiscalDocuments: FiscalDocument[];
   readonly fiscalPrintEnabled: boolean;
};

export const PrivateIndividualInvoiceCreateModalConfirmButton = (
   props: PrivateIndividualInvoiceCreateModalConfirmButtonProps
): JSX.Element => {
   const {
      onClose,
      fiscalDocuments,
      fiscalPrintEnabled,
      handleSubmit,
      paymentMethod,
      visitDate,
      visitSettlementId,
   } = props;

   const receiptAvailableForInvoice = useMemo(
      () =>
         fiscalPrintEnabled &&
         fiscalDocumentTypeVisitor(fiscalDocuments)({
            receiptWithTaxIdentifier: () => true,
            receiptWithoutTaxIdentifier: () => true,
            noFiscalDocuments: () => false,
         }),
      [fiscalDocuments, fiscalPrintEnabled]
   );

   const { executeAsync: createPrivateIndividualInvoice } = usePrivateIndividualInvoiceCreateCommand({
      onSuccess: onClose,
      paymentMethod: paymentMethod,
      visitDate: visitDate,
      visitSettlementId: visitSettlementId,
      receiptAvailableForInvoice: receiptAvailableForInvoice,
   });

   const shouldAskForReceiptOnConfirm = useMemo(
      () =>
         fiscalPrintEnabled &&
         fiscalDocumentTypeVisitor(fiscalDocuments)({
            receiptWithTaxIdentifier: () => false,
            receiptWithoutTaxIdentifier: () => false,
            noFiscalDocuments: () => true,
         }),
      [fiscalDocuments, fiscalPrintEnabled]
   );

   if (shouldAskForReceiptOnConfirm) {
      return (
         <WithReceiptModalOpener
            onClose={onClose}
            handleSubmit={handleSubmit}
            paymentMethod={paymentMethod}
            visitDate={visitDate}
            visitSettlementId={visitSettlementId}
         >
            {({ openModal }) => (
               <ModalConfirmButton
                  testId={'privateIndividualInvoiceCreateWithReceiptModalConfirmButton'}
                  label={invoiceCreateModalTranslationKeys.confirmButtonLabel}
                  onClick={handleSubmit(openModal)}
               />
            )}
         </WithReceiptModalOpener>
      );
   }

   return (
      <ModalConfirmButton
         testId={'privateIndividualInvoiceCreateModalConfirmButton'}
         label={invoiceCreateModalTranslationKeys.confirmButtonLabel}
         onClick={handleSubmit(createPrivateIndividualInvoice)}
      />
   );
};
